import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { ClaimNumberFilter } from '../../../../models/api/reports/claim-number-filter';
import { ClaimNumberFilterInput } from '../../../../models/api/reports/claim-number-filter-input';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { ReportsService } from '../../../../services/reports/reports.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { CheckboxComponent } from '../../../core/controls/checkbox.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DialogComponent } from '../../../core/panels/dialog.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { ClaimNumbersComponent } from './claim-numbers.component';

@Component({
  selector: 'app-claim-number-criteria',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    ClaimNumbersComponent,
    DialogComponent,
    InputComponent,
    RadioButtonComponent,
    CheckboxComponent,
  ],
  template: `
    <div class="tw-pt-0">
      <app-action-row>
        <div
          left1
          class="tw-flex tw-gap-2 tw-justify-center sm:tw-justify-start"
        >
          <app-button label="Add" emphasis="Low" (click)="openDialog()" />
          <app-button label="Remove all" emphasis="Low" (click)="deleteAll()" />
        </div>
        <app-radio-button
          right1
          [control]="fg.controls.selectMode"
          screenReaderLabel="Selection mode"
          optionLabel="name"
          optionValue="code"
          [options]="selectModes"
          [showHelpText]="false"
          [vertical]="false"
        />
      </app-action-row>
      <app-claim-numbers
        [dataSource]="selectedClaimNumbers"
        (removed)="onClaimRemoved($event)"
      ></app-claim-numbers>
    </div>
    <app-dialog
      #dialog
      [visible]="dialogVisible"
      [closable]="false"
      (show)="show()"
      label="Add claim numbers"
      [autoFocus]="false"
    >
      <ng-template #content>
        <form [formGroup]="fg">
          <div
            class="tw-w-full tw-italic tw-pb-4 tw-mb-2 tw-text-gray-600 tw-max-w-[800px]"
          >
            Enter a claim number or alternate claim number in the search field
            to see all matches that begin with your search text. Select the
            Exact match checkbox to find a claim number or alternate claim
            number that matches your search exactly.
          </div>
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-items-start tw-gap-4"
          >
            <app-input
              [control]="fg.controls.claimNumber"
              autofocus
              label="Claim number"
              class="tw-w-full md:tw-flex-1"
              helpText="Search requires at least 4 characters"
              #claimNumberInput
            />
            <div class="tw-pt-2">
              <app-checkbox
                [control]="fg.controls.exactMatch"
                label="Exact match"
              />
            </div>
          </div>
        </form>

        <app-action-row>
          <app-button
            right1
            type="submit"
            label="Search"
            (click)="search()"
            [disabled]="this.fg.controls.claimNumber.value?.length < 4"
            [loading]="loading"
          />
          <app-button
            right2
            emphasis="Low"
            label="Clear"
            (click)="clearSearch()"
          />
        </app-action-row>
        <div class="tw-min-h-[200px]">
          <app-claim-numbers
            [dataSource]="claimNumberSearchResults"
            [showRemove]="false"
            [showSelection]="true"
            paginatorDropdownAppendTo="self"
            [loading]="loading"
            (selected)="onSelectionChanged($event)"
          />
        </div>
        @if (maxClaimsError) {
          <div
            class="tw-text-[var(--sed-status-red-color)] tw-font-bold tw-text-[80%] tw-px-4 tw-pt-1 tw-pb-2 tw-leading-[14px]"
            role="alert"
            aria-live="polite"
          >
            {{ maxClaimsError }}
          </div>
        }
        <app-action-row>
          <app-button
            [disabled]="!selectedClaimCount"
            [badgeLabel]="selectedClaimCount ?? null"
            right1
            label="Add"
            (click)="addClaimNumber()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: ``,
})
export class ClaimNumberCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @ViewChild('claimNumberInput')
  claimNumberInput: InputComponent;

  @Input({ required: true })
  section: FilterSection;

  dialogVisible = false;

  selectedClaimCount = 0;

  contractNumber: number;
  claimNumbers: any[] = [];
  selectedClaimNumbers: any[] = [];
  selectedRows: ClaimNumberFilter[] = [];
  claimNumberSearch: ClaimNumberFilterInput;
  claimNumberSearchResults: ClaimNumberFilter[] = [];
  maxClaimsError = '';

  loading = false;

  private reports = inject(ReportsService);

  fg = new FormGroup({
    claimNumber: new FormControl<string>(null, []),
    exactMatch: new FormControl<boolean>(false),
    selectMode: new FormControl<string>('include'),
  });

  selectModes = [
    { name: 'Include', code: 'include' },
    { name: 'Exclude', code: 'exclude' },
  ];

  constructor() {
    super();
  }

  @HandleLoadingState()
  async setClaimNumberFromCriteria(criteria: FilterCriteria[]) {
    const claimNumbers = criteria
      .filter((c) => c.column === 'FILE_NUMBER')
      .flatMap((c) => c.values);

    if (!claimNumbers.length) return;

    this.selectedClaimNumbers = await this.api.filtersCriteria.getClaimNumbers(
      this.contractNumber,
      claimNumbers,
    );

    if (criteria[0]?.operator === 'NOTEQUALTO') {
      this.fg.controls.selectMode.setValue('exclude', { emitEvent: false });
    } else {
      this.fg.controls.selectMode.setValue('include', { emitEvent: false });
    }

    this.emitCount();
  }

  async ngOnInit() {
    this.section.criteria = this;

    const client = this.reports.selectedClient();
    if (client) {
      this.contractNumber = Number(client);
    }

    if (this.section.values?.length) {
      this.setClaimNumberFromCriteria(this.section.values);
    }
  }

  valid(): boolean {
    return true;
  }

  openDialog() {
    this.dialogVisible = true;
  }

  onSelectionChanged(event: any) {
    this.selectedRows = event;
    this.selectedClaimCount = event.length;
  }

  show() {
    this.claimNumberInput.focus();
    this.fg.controls.claimNumber.reset();
    this.fg.controls.exactMatch.reset();
  }

  onClaimRemoved(row: any) {
    this.selectedClaimNumbers = this.selectedClaimNumbers.filter(
      (claim) => claim.claimNumber !== row.claimNumber,
    );

    this.section.count.set(this.selectedClaimNumbers.length);
  }

  addClaimNumber() {
    const totalAfterAdd =
      this.selectedClaimNumbers.length + this.selectedRows.length;
    if (totalAfterAdd > 200) {
      this.maxClaimsError = `Error: The maximum of 200 claims have been added`;
      return;
    }

    this.maxClaimsError = '';
    this.selectedClaimNumbers = [
      ...this.selectedClaimNumbers,
      ...this.selectedRows,
    ];

    this.dialogVisible = false;
    this.selectedClaimCount = 0;
    this.claimNumberSearchResults = [];
    this.fg.controls.claimNumber.reset();
    this.fg.controls.exactMatch.reset();
    this.maxClaimsError = '';
    this.emitCount();
  }

  closeDialog() {
    this.dialogVisible = false;
    this.selectedClaimCount = 0;
    this.claimNumberSearchResults = [];
    this.maxClaimsError = '';
    this.fg.controls.claimNumber.reset();
    this.fg.controls.exactMatch.reset();
  }

  deleteAll() {
    this.selectedClaimNumbers = [];
    this.maxClaimsError = '';
    this.section.count.set(0);
  }

  clearSearch() {
    this.fg.controls.claimNumber.reset();
    this.fg.controls.exactMatch.reset();
    this.claimNumberSearchResults = [];
    this.selectedRows = [];
    this.selectedClaimCount = 0;
  }

  @HandleLoadingState()
  async search() {
    if (!this.fg.controls.claimNumber.valid) {
      this.fg.controls.claimNumber.markAsTouched();
      return;
    }
    this.claimNumberSearch = {
      searchText: this.fg.controls.claimNumber.value,
      exactMatch: this.fg.controls.exactMatch.value,
      contractNumber: this.contractNumber,
    };
    const results = await this.api.filtersCriteria.searchClaimNumber(
      this.claimNumberSearch,
    );
    this.claimNumberSearchResults = results.filter(
      (r) =>
        !this.selectedClaimNumbers.some(
          (existing) => existing.claimNumber === r.claimNumber,
        ),
    );
  }

  private emitCount() {
    const count = this.selectedClaimNumbers.length;
    this.section.count.set(count);
  }

  getCriteria(): FilterCriteria[] {
    return this.selectedClaimNumbers.map(
      (claimNumbers) =>
        ({
          tableAlias: 'claim',
          column: 'FILE_NUMBER',
          displayName: '',
          operator:
            this.fg.controls.selectMode.value === 'include'
              ? 'EQUALS'
              : 'NOTEQUALTO',
          values: [claimNumbers.claimNumber],
          lineOfBusiness: '',
          dataType: '',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Claim Number',
          filterSubsection: '',
        }) as FilterCriteria,
    );
  }

  clearCriteria(): void {
    this.selectedClaimNumbers = [];
    this.fg.controls.selectMode.setValue('include', { emitEvent: false });
    this.maxClaimsError = '';
    this.section.count.set(0);
  }
}
